import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "axios";

import Navbar from "./Navbar";
import Footer from "./Footer";

import { AppContext } from "../context/AppContext";
import useNavigateWithLang from "../hooks/useNavigateWithLang";
import { useLayoutConfig } from "../hooks/useLayoutConfig";

import { saveGuest, type GuestUpdateFieldError } from "../api/GuestService";
import { fetchReservation } from "../api/ReservationService";

import { mapGuestsFromReservation } from "../utils/GuestMapper";
import { enrichGuests, getLovForField, isOperaGuestProfileId } from "../utils/guestUtils";
import { isGuestSlotChild } from "../utils/guestSlotAgeValidation";
import { useBrandAppearance } from "../hooks/useBrandAppearance";
import { getTenantConfig } from "../config/tenant";

const Layout: React.FC = () => {
    useBrandAppearance();
    const { state, dispatch, setCurrentStep, setIsFormDirty } = useContext(AppContext) ?? {};
    const location = useLocation();
    const { navigateWithLang } = useNavigateWithLang();
    const { hideFooter = false, hideNavbar = false } = useLayoutConfig();

    const [isSaving, setIsSaving] = useState(false);

    const steps = ["guests", ...(state?.paymentEnabled ? ["payment"] : []), "checkIn"];

    const handleSave = async () => {
        if (isSaving) return; // 🚫 ignore double-clicks
        setIsSaving(true);

        const guestIndexFromUrl = Number(location.pathname.split("/").pop()) - 1;

        if (
            isNaN(guestIndexFromUrl) ||
            guestIndexFromUrl < 0 ||
            guestIndexFromUrl >= (state?.guests?.length || 0)
        ) {
            toast.error("Guest not found");
            setIsSaving(false);
            return;
        }

        const currentGuest = state.guests[guestIndexFromUrl];
        const isSyncedSubmission = currentGuest.syncStatus === "SYNCED";
        const isNewGuest = !isSyncedSubmission && !isOperaGuestProfileId(
            currentGuest.id,
            currentGuest.checkInSubmissionId
        );
        const guestToSave = { ...currentGuest, completed: true };

        // Persist the adult/child intent of the URL slot the user clicked so the public UI can
        // bucket guests by kind on refresh — a child saved here will never re-render in an adult
        // slot, even if Opera returns the share list in a different order.
        const guestKindForCreate =
            isNewGuest
                ? isGuestSlotChild(state.adultCount, guestIndexFromUrl)
                    ? ("CHILD" as const)
                    : ("ADULT" as const)
                : undefined;

        try {
            const confirmationNumber = sessionStorage.getItem("confirmationNumber") || "";
            const lastName = sessionStorage.getItem("lastName") ?? "";

            // Resolve the per-field address-country LOV (defaults preserve pre-V14 behaviour;
            // chains that flipped addressCountry to NATIONALITY get validated against the
            // NATIONALITY map here instead of countries).
            const addressCountryLov = getLovForField(
                "addressCountry",
                getTenantConfig()?.lovSources,
                { nationalities: state.nationalities, countries: state.countries }
            );

            const { profileId: savedId, summary } = await saveGuest(
                guestToSave,
                state.hotelId,
                state.reservationId,
                confirmationNumber,
                isNewGuest ? state.mainGuestId : undefined,
                state.titles,
                { addressCountryLov },
                guestKindForCreate
            );

            const submissionIdMeta =
                savedId != null && savedId < 0
                    ? Math.abs(savedId)
                    : guestToSave.checkInSubmissionId;

            // ✅ Optimistic local update so cards keep their data/“complete” state immediately
            const optimistic = {
                ...guestToSave,
                id: savedId ?? guestToSave.id,
                ...(submissionIdMeta != null ? { checkInSubmissionId: submissionIdMeta } : {}),
                completed: true,
            };

            dispatch({
                type: "SET_GUESTS",
                payload: state.guests.map((g, i) => (i === guestIndexFromUrl ? optimistic : g)),
            });

            // If we don't have enough data to refetch, proceed with optimistic state
            if (!lastName || !confirmationNumber) {
                toast.error("Missing reservation data");
                setIsFormDirty?.(false);
                handleProceed();
                return;
            }

            // Refetch reservation -> map -> hydrate to full guests (fetchGuestById)
            const previousGuests = state.guests;
            const updatedReservation = await fetchReservation(lastName, confirmationNumber);
            const mapped = await mapGuestsFromReservation(updatedReservation);
            const hydrated = await enrichGuests(mapped);

            // The hydrated row for the just-saved slot is sourced from Opera FetchProfile, which
            // may still reflect pre-save state until our PENDING UPDATE syncs. We force the
            // optimistic "completed" badge to stick for that slot (the user just submitted with
            // all required fields), and also preserve completion for any previously-saved slot
            // so a second save in the same session doesn't make earlier checkmarks disappear.
            const finalGuests =
                hydrated && hydrated.length
                    ? hydrated.map((g, i) => {
                          if (i === guestIndexFromUrl) {
                              return { ...g, completed: true };
                          }
                          const prev = previousGuests[i];
                          return prev?.completed ? { ...g, completed: true } : g;
                      })
                    : mapped.map((g, i) => (i === guestIndexFromUrl ? optimistic : g));

            dispatch({ type: "SET_GUESTS", payload: finalGuests });

            if (summary.hasErrors && summary.fieldErrors.length > 0) {
                const failedLines = summary.fieldErrors.map((e) => e.message).join("\n");
                if (summary.succeededSteps.length > 0) {
                    toast(
                        `Partial save — not all fields were updated.\n\n${failedLines}\n\nUpdated: ${summary.succeededSteps.join(", ")}`,
                        { duration: 14000 }
                    );
                } else {
                    toast.error(failedLines, { duration: 14000 });
                }
            } else {
                toast.success("Guest saved successfully");
            }
            setIsFormDirty?.(false);
            handleProceed();
        } catch (err) {
            console.error("❌ Error saving guest:", err);
            if (axios.isAxiosError(err) && err.response?.status === 422) {
                const data = err.response.data as {
                    fieldErrors?: GuestUpdateFieldError[];
                    message?: string;
                };
                const msg =
                    data.fieldErrors?.map((e) => e.message).join("\n") ??
                    data.message ??
                    "All updates were rejected by the hotel system.";
                toast.error(msg, { duration: 14000 });
            } else {
                toast.error("Failed to save guest");
            }
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (location.pathname.includes("/guest-details")) {
            setCurrentStep(1);
        } else if (location.pathname.includes("/payment")) {
            setCurrentStep(2);
        } else if (location.pathname.includes("/check-in-complete")) {
            setCurrentStep(state?.paymentEnabled ? 3 : 2);
        }
    }, [location.pathname, setCurrentStep, state?.paymentEnabled]);

    const handleProceed = () => {
        if (state.paymentEnabled) {
            switch (state.currentStep) {
                case 1:
                    setCurrentStep(2);
                    navigateWithLang("/payment");
                    break;
                case 2:
                    setCurrentStep(3);
                    navigateWithLang("/check-in-complete");
                    break;
                default:
                    break;
            }
        } else if (state.currentStep === 1) {
            setCurrentStep(2);
            navigateWithLang("/check-in-complete");
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100dvh" }}>
            {!hideNavbar && (
                <Box sx={{ flexShrink: 0 }}>
                    <Navbar />
                </Box>
            )}

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                <Outlet />
            </Box>

            {!hideFooter && (
                <Box sx={{ flexShrink: 0 }}>
                    <Footer
                        currentStep={state.currentStep}
                        steps={steps}
                        onProceed={handleProceed}
                        onSave={handleSave}
                        isSaving={isSaving} // ⬅️ pass down
                    />
                </Box>
            )}
        </Box>
    );
};

export default Layout;
